import { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useAnnounce } from "@/components/a11y/Announcer";
import { useChatStore } from "@/stores/chatStore";
import { useMapStore } from "@/stores/mapStore";
import type { ChatMessage } from "@/types";
import { ChatInput } from "./ChatInput";
import { MessageBubble } from "./MessageBubble";
import { PropertyCard } from "./PropertyCard";
import { CompanyCard } from "./CompanyCard";
import { MunicipalityCard } from "./MunicipalityCard";
import { ThinkingBreadcrumb } from "./ThinkingBreadcrumb";
import { ToolActivity } from "./ToolActivity";
import { WelcomeState } from "./WelcomeState";
import { useBreakpoint } from "@/hooks/useBreakpoint";

function estimateMessageSize(message: ChatMessage): number {
  const len = message.content.length;
  const hasCode = message.content.includes("```");
  if (len < 100) return 80;
  if (len < 500 || hasCode) return 180;
  return 320;
}

interface ChatPanelProps {
  onSendMessage: (content: string) => void;
  onEntityClick?: (entityType: string, entityId: string, name: string) => void;
  onStopStreaming?: () => void;
  disabled?: boolean;
}

export function ChatPanel({ onSendMessage, onEntityClick, onStopStreaming, disabled }: ChatPanelProps) {
  const { t } = useTranslation();
  const { messages, isStreaming, activeTools, thinkingSteps, disconnectedWhileStreaming, lastUserMessage } = useChatStore();
  const setDisconnectedWhileStreaming = useChatStore((s) => s.setDisconnectedWhileStreaming);

  const clickedLocation = useMapStore((s) => s.clickedLocation);
  const clearClickedLocation = useMapStore((s) => s.clearClickedLocation);
  const drawnPolygon = useMapStore((s) => s.drawnPolygon);
  const clearDrawnPolygon = useMapStore((s) => s.clearDrawnPolygon);
  const isDrawing = useMapStore((s) => s.isDrawing);
  const setIsDrawing = useMapStore((s) => s.setIsDrawing);
  const breakpoint = useBreakpoint();
  const { announce } = useAnnounce();
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => estimateMessageSize(messages[i]!),
    overscan: 5,
  });

  const isDisconnected = !!disabled;
  const showRetryBanner = disconnectedWhileStreaming && !isDisconnected && lastUserMessage;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distanceFromBottom < 80);
  }, []);

  useEffect(() => {
    if (isAtBottom && messages.length > 0) {
      // Use bottomRef for tool activity (outside virtualizer), virtualizer for messages
      if (activeTools.length > 0) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      } else {
        virtualizer.scrollToIndex(messages.length - 1, { align: "end", behavior: "smooth" });
      }
    }
  }, [messages, activeTools, isAtBottom, isStreaming, virtualizer]);

  // Announce reply completion to screen readers
  const prevStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming && messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last && last.role === "assistant" && last.content) {
        const preview = last.content.slice(0, 100);
        announce(`Reply: ${preview}${last.content.length > 100 ? "..." : ""}`);
      }
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming, messages, announce]);

  // Announce connection loss
  useEffect(() => {
    if (disconnectedWhileStreaming) {
      announce("Connection lost. Your response was interrupted.", "assertive");
    }
  }, [disconnectedWhileStreaming, announce]);

  function handleEditMessage(_messageId: string, newContent: string) {
    useChatStore.getState().resetChat();
    useMapStore.getState().clearFeatures();
    onSendMessage(newContent);
  }

  function scrollToBottom() {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  function handleRetry() {
    if (lastUserMessage) {
      setDisconnectedWhileStreaming(false);
      onSendMessage(lastUserMessage);
    }
  }

  function handleDismiss() {
    setDisconnectedWhileStreaming(false);
  }

  function handleRetryLastMessage() {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg && msg.role === "user") {
        onSendMessage(msg.content);
        return;
      }
    }
  }

  return (
    <div className="cx-chat-surface flex h-full flex-col relative">
      {/* Messages */}
      <div className="relative flex-1 overflow-hidden">
      <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto" aria-busy={isStreaming}>
        <div className="mx-auto w-full max-w-3xl px-5 py-6">
          {messages.length === 0 ? (
            <WelcomeState onSend={onSendMessage} onDrawToggle={() => setIsDrawing(!isDrawing)} />
          ) : (
            <>
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const msg = messages[virtualRow.index]!;
                  const i = virtualRow.index;
                  return (
                    <div
                      key={msg.id}
                      ref={virtualizer.measureElement}
                      data-index={i}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                        paddingBottom: "20px",
                      }}
                    >
                      {msg.role === "assistant" && msg.propertyBlock && (
                          <div className="pb-5">
                            <PropertyCard data={msg.propertyBlock} />
                          </div>
                        )}
                      {msg.role === "assistant" && msg.companyBlock && (
                          <div className="pb-5">
                            <CompanyCard data={msg.companyBlock} />
                          </div>
                        )}
                      {msg.role === "assistant" && msg.municipalityBlock && (
                          <div className="pb-5">
                            <MunicipalityCard data={msg.municipalityBlock} />
                          </div>
                        )}
                      {msg.role === "assistant" && msg.thinkingSteps && msg.thinkingSteps.length > 0 && !isStreaming && (
                        <ThinkingBreadcrumb
                          steps={msg.thinkingSteps}
                          isStreaming={false}
                          hasContent={true}
                        />
                      )}
                      <MessageBubble
                        message={msg}
                        isLast={i === messages.length - 1}
                        breakpoint={breakpoint}
                        onEntityClick={onEntityClick}
                        onRetry={
                          i === messages.length - 1 && msg.role === "assistant" && !isStreaming
                            ? handleRetryLastMessage
                            : undefined
                        }
                        onEdit={
                          msg.role === "user" && !isStreaming
                            ? (newContent: string) => handleEditMessage(msg.id, newContent)
                            : undefined
                        }
                      />
                    </div>
                  );
                })}
              </div>
              {isStreaming && thinkingSteps.length > 0 && (
                <div className="pb-1">
                  <ThinkingBreadcrumb
                    steps={thinkingSteps}
                    isStreaming={true}
                    hasContent={(messages[messages.length - 1]?.content?.length ?? 0) > 0}
                  />
                </div>
              )}
              {activeTools.length > 0 && (
                <div className="pb-5">
                  <ToolActivity tools={activeTools} />
                </div>
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>
      </div>

      {/* Scroll to bottom — positioned relative to the scroll wrapper above */}
      {!isAtBottom && messages.length > 0 && (
        <div className="cx-scroll-bottom-wrap">
          <button
            onClick={scrollToBottom}
            className="cx-msg-action cx-scroll-bottom-btn animate-cx-fade-in"
            aria-label="Scroll to bottom"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>
      )}
      </div>

      {/* Retry banner */}
      {showRetryBanner && (
        <div className="mx-auto w-full max-w-3xl px-5">
          <div className="cx-retry-banner mb-2 flex items-center justify-between rounded-lg px-4 py-2.5 text-[13px]">
            <span className="cx-text-secondary">
              {t("chat.retryPrompt")}
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleRetry}
                className="cx-retry-btn rounded-md px-3 py-1 text-[12px] font-semibold transition-colors"
              >
                {t("chat.retry")}
              </button>
              <button
                onClick={handleDismiss}
                className="cx-dismiss-btn rounded-md px-3 py-1 text-[12px] transition-colors"
              >
                {t("chat.dismiss")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnected indicator */}
      {isDisconnected && messages.length > 0 && (
        <div className="mx-auto w-full max-w-3xl px-5">
          <div className="cx-text-secondary mb-2 rounded-lg px-4 py-2 text-center text-[12px]">
            {t("chat.disconnected")}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="mx-auto w-full max-w-3xl">
        <ChatInput
          onSend={onSendMessage}
          disabled={!!disabled}
          isStreaming={isStreaming}
          onStop={onStopStreaming}
          location={clickedLocation}
          onClearLocation={clearClickedLocation}
          drawnPolygon={drawnPolygon}
          onClearPolygon={clearDrawnPolygon}
          isDrawing={isDrawing}
          onDrawToggle={() => setIsDrawing(!isDrawing)}
        />
      </div>
    </div>
  );
}
